import React, { useEffect, useState } from 'react';
import { Modal, Input, Radio, Select, Steps } from 'antd';
import communicationsService from '../../services/communicationsService.js';
import { MOCK_PROJECT_NUMBER } from '../../services/communications/communicationsMockData.js';

const { Step } = Steps;

export default function CommunicationsSetupModal({
  open,
  onClose,
  projectName,
  members = [],
  onComplete,
}) {
  const [step, setStep] = useState(0);
  const [outlookMode, setOutlookMode] = useState('create');
  const [teamsMode, setTeamsMode] = useState('create');
  const [plannerMode, setPlannerMode] = useState('create');
  const [outlookName, setOutlookName] = useState(`${MOCK_PROJECT_NUMBER} - ${projectName || 'Project'}`);
  const [teamsName, setTeamsName] = useState(`${projectName || 'Project'} - Project Team`);
  const [teamsChannel, setTeamsChannel] = useState('#general');
  const [plannerName, setPlannerName] = useState(`${projectName || 'Project'} Deliverables`);
  const [plannerTemplate, setPlannerTemplate] = useState('p1');
  const [outlookExisting, setOutlookExisting] = useState('');
  const [teamsExisting, setTeamsExisting] = useState('');
  const [plannerExisting, setPlannerExisting] = useState('');
  const [pickers, setPickers] = useState({ outlook: [], teams: [], planner: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [outlook, teams, planner] = await Promise.all([
        communicationsService.listAvailableMicrosoftResources('preview', 'outlook'),
        communicationsService.listAvailableMicrosoftResources('preview', 'teams'),
        communicationsService.listAvailableMicrosoftResources('preview', 'planner'),
      ]);
      setPickers({ outlook, teams, planner });
    })();
  }, [open]);

  const resetAndClose = () => {
    setStep(0);
    onClose?.();
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      const config = {
        outlook: {
          mode: outlookMode,
          name: outlookName,
          selectedExistingId: outlookMode === 'link' ? outlookExisting : undefined,
        },
        teams: {
          mode: teamsMode,
          name: teamsMode === 'link'
            ? pickers.teams.find((t) => t.id === teamsExisting)?.label
            : teamsName,
          selectedExistingId: teamsMode === 'link' ? teamsExisting : undefined,
          channel: teamsChannel,
        },
        planner: {
          mode: plannerMode,
          name: plannerMode === 'link'
            ? pickers.planner.find((p) => p.id === plannerExisting)?.label
            : plannerName,
          selectedExistingId: plannerMode === 'link' ? plannerExisting : undefined,
          template: plannerTemplate,
        },
        inviteMemberIds: members.map((m) => m.id).filter(Boolean),
      };
      await onComplete?.(config);
      resetAndClose();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTeam = pickers.teams.find((t) => t.id === teamsExisting);

  return (
    <Modal
      title="Configure project communications"
      open={open}
      onCancel={resetAndClose}
      width={640}
      footer={null}
      className="comm-setup-modal"
    >
      <p className="comm-setup-modal__intro">
        Link Outlook, Teams, and Planner for this project. Available for Microsoft Entra (Azure AD) users.
      </p>

      <Steps current={step} size="small" style={{ marginBottom: 24 }}>
        <Step title="Outlook" />
        <Step title="Teams" />
        <Step title="Planner" />
        <Step title="Review" />
      </Steps>

      {step === 0 ? (
        <div className="comm-setup-step">
          <Radio.Group value={outlookMode} onChange={(e) => setOutlookMode(e.target.value)}>
            <Radio value="create">Create new folder</Radio>
            <Radio value="link">Select existing folder</Radio>
          </Radio.Group>
          {outlookMode === 'create' ? (
            <Input
              style={{ marginTop: 12 }}
              value={outlookName}
              onChange={(e) => setOutlookName(e.target.value)}
              placeholder="Folder name"
            />
          ) : (
            <Select
              style={{ width: '100%', marginTop: 12 }}
              placeholder="Select folder"
              value={outlookExisting || undefined}
              onChange={setOutlookExisting}
              options={pickers.outlook.map((f) => ({ value: f.id, label: f.label }))}
            />
          )}
        </div>
      ) : null}

      {step === 1 ? (
        <div className="comm-setup-step">
          <Radio.Group value={teamsMode} onChange={(e) => setTeamsMode(e.target.value)}>
            <Radio value="create">Create new Team</Radio>
            <Radio value="link">Link existing Team</Radio>
          </Radio.Group>
          {teamsMode === 'create' ? (
            <Input
              style={{ marginTop: 12 }}
              value={teamsName}
              onChange={(e) => setTeamsName(e.target.value)}
              placeholder="Team name"
            />
          ) : (
            <Select
              style={{ width: '100%', marginTop: 12 }}
              placeholder="Select team"
              value={teamsExisting || undefined}
              onChange={setTeamsExisting}
              options={pickers.teams.map((t) => ({ value: t.id, label: t.label }))}
            />
          )}
          <Select
            style={{ width: '100%', marginTop: 12 }}
            value={teamsChannel}
            onChange={setTeamsChannel}
            options={(teamsMode === 'link' && selectedTeam?.channels
              ? selectedTeam.channels
              : ['#general', '#structural', '#mep']
            ).map((c) => ({ value: c, label: c }))}
          />
        </div>
      ) : null}

      {step === 2 ? (
        <div className="comm-setup-step">
          <Radio.Group value={plannerMode} onChange={(e) => setPlannerMode(e.target.value)}>
            <Radio value="create">Create new Plan</Radio>
            <Radio value="link">Link existing Plan</Radio>
          </Radio.Group>
          {plannerMode === 'create' ? (
            <>
              <Input
                style={{ marginTop: 12 }}
                value={plannerName}
                onChange={(e) => setPlannerName(e.target.value)}
                placeholder="Plan name"
              />
              <Select
                style={{ width: '100%', marginTop: 12 }}
                value={plannerTemplate}
                onChange={setPlannerTemplate}
                options={pickers.planner.map((p) => ({ value: p.id, label: p.label }))}
              />
            </>
          ) : (
            <Select
              style={{ width: '100%', marginTop: 12 }}
              placeholder="Select plan"
              value={plannerExisting || undefined}
              onChange={setPlannerExisting}
              options={pickers.planner.map((p) => ({ value: p.id, label: p.label }))}
            />
          )}
        </div>
      ) : null}

      {step === 3 ? (
        <div className="comm-setup-step">
          <p><strong>Outlook:</strong> {outlookMode === 'create' ? outlookName : pickers.outlook.find((f) => f.id === outlookExisting)?.label}</p>
          <p><strong>Teams:</strong> {teamsMode === 'create' ? teamsName : selectedTeam?.label} {teamsChannel}</p>
          <p><strong>Planner:</strong> {plannerMode === 'create' ? plannerName : pickers.planner.find((p) => p.id === plannerExisting)?.label}</p>
          <p style={{ marginTop: 16 }}>
            <strong>{members.length || 'All project'}</strong> company member(s) on this project will be invited.
          </p>
        </div>
      ) : null}

      <div className="comm-setup-modal__footer">
        {step > 0 ? (
          <button type="button" className="btn-secondary" onClick={() => setStep((s) => s - 1)}>
            Back
          </button>
        ) : (
          <span />
        )}
        {step < 3 ? (
          <button type="button" className="btn-primary" onClick={() => setStep((s) => s + 1)}>
            Next
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={handleFinish}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Complete setup'}
          </button>
        )}
      </div>
    </Modal>
  );
}
